import React, { useState, useRef, useEffect } from "react";
import { ArrowLeft, FileText, Users, Download, Copy, CheckCircle, AlertTriangle, Plus, Trash2, Percent } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Separator } from "../components/ui/separator";
import { Link } from "react-router-dom";

// PRO options for dropdown
const proOptions = ["BMI", "ASCAP", "SESAC", "PRS", "SOCAN", "GEMA", "OTHER"];

// Writer form component
const WriterForm = ({ writer, index, onChange, onRemove, canRemove }) => {
  const handleChange = (field, value) => {
    onChange(index, { ...writer, [field]: value });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.08)] rounded-xl p-5 sm:p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <h3
          className="text-[rgb(218,255,1)] text-sm font-semibold tracking-[0.1em]"
          style={{ fontFamily: "'Oswald', sans-serif" }}
        >
          WRITER {index + 1}
        </h3>
        {canRemove && (
          <button
            onClick={() => onRemove(index)}
            className="text-[rgb(100,100,110)] hover:text-red-400 transition-colors p-1"
            data-testid={`remove-writer-${index}`}
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Legal Name */}
        <div className="sm:col-span-2">
          <label className="text-[rgb(161,161,170)] text-[10px] tracking-[0.15em] block mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
            LEGAL NAME *
          </label>
          <Input
            value={writer.legalName}
            onChange={(e) => handleChange("legalName", e.target.value)}
            placeholder="Full legal name as registered with PRO"
            data-testid={`writer-${index}-legal-name`}
            className="bg-[rgb(40,42,45)] border-[rgb(63,63,63)] text-white placeholder:text-[rgb(80,80,90)] focus:border-[rgb(218,255,1)] focus:ring-[rgba(218,255,1,0.1)]"
          />
        </div>

        {/* PRO */}
        <div>
          <label className="text-[rgb(161,161,170)] text-[10px] tracking-[0.15em] block mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
            PRO AFFILIATION *
          </label>
          <select
            value={writer.pro}
            onChange={(e) => handleChange("pro", e.target.value)}
            data-testid={`writer-${index}-pro`}
            className="w-full h-10 px-3 rounded-md bg-[rgb(40,42,45)] border border-[rgb(63,63,63)] text-white text-sm focus:border-[rgb(218,255,1)] focus:outline-none focus:ring-1 focus:ring-[rgba(218,255,1,0.1)] cursor-pointer"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <option value="">Select PRO</option>
            {proOptions.map((pro) => (
              <option key={pro} value={pro}>{pro}</option>
            ))}
          </select>
        </div>

        {/* IPI Number */}
        <div>
          <label className="text-[rgb(161,161,170)] text-[10px] tracking-[0.15em] block mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
            IPI NUMBER *
          </label>
          <Input
            value={writer.ipi}
            onChange={(e) => handleChange("ipi", e.target.value)}
            placeholder="00000000000"
            data-testid={`writer-${index}-ipi`}
            className="bg-[rgb(40,42,45)] border-[rgb(63,63,63)] text-white placeholder:text-[rgb(80,80,90)] focus:border-[rgb(218,255,1)] focus:ring-[rgba(218,255,1,0.1)] font-mono"
          />
        </div>

        {/* Split Percentage */}
        <div className="sm:col-span-2">
          <label className="text-[rgb(161,161,170)] text-[10px] tracking-[0.15em] block mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
            OWNERSHIP SPLIT (%) *
          </label>
          <div className="relative">
            <Input
              type="number"
              min="0"
              max="100"
              value={writer.split}
              onChange={(e) => handleChange("split", e.target.value)}
              placeholder="50"
              data-testid={`writer-${index}-split`}
              className="bg-[rgb(40,42,45)] border-[rgb(63,63,63)] text-white placeholder:text-[rgb(80,80,90)] focus:border-[rgb(218,255,1)] focus:ring-[rgba(218,255,1,0.1)] pr-10"
            />
            <Percent size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[rgb(100,100,110)]" />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const SplitSheetPage = () => {
  useEffect(() => {
    const m = document.createElement('meta');
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => document.head.removeChild(m);
  }, []);

  const [trackTitle, setTrackTitle] = useState("");
  const [creationDate, setCreationDate] = useState("");
  const [writers, setWriters] = useState([
    { legalName: "", pro: "", ipi: "", split: "" },
    { legalName: "", pro: "", ipi: "", split: "" },
  ]);
  const [generated, setGenerated] = useState(false);
  const [copied, setCopied] = useState(false);
  const [splitError, setSplitError] = useState(false);
  const outputRef = useRef(null);

  const handleWriterChange = (index, updatedWriter) => {
    const newWriters = [...writers];
    newWriters[index] = updatedWriter;
    setWriters(newWriters);
  };

  const addWriter = () => {
    if (writers.length < 6) {
      setWriters([...writers, { legalName: "", pro: "", ipi: "", split: "" }]);
    }
  };

  const removeWriter = (index) => {
    if (writers.length > 2) {
      setWriters(writers.filter((_, i) => i !== index));
    }
  };

  const calculateTotalSplit = () => {
    return writers.reduce((sum, w) => sum + (parseFloat(w.split) || 0), 0);
  };

  const isFormValid = () => {
    if (!trackTitle.trim() || !creationDate) return false;
    const totalSplit = calculateTotalSplit();
    if (totalSplit !== 100) return false;
    return writers.every(w => 
      w.legalName.trim() && w.pro && w.ipi.trim() && w.split
    );
  };

  const generateSplitSheet = () => {
    const totalSplit = calculateTotalSplit();
    if (totalSplit !== 100) {
      setSplitError(true);
      setTimeout(() => setSplitError(false), 3000);
      return;
    }
    setGenerated(true);
  };

  const getSplitSheetText = () => {
    const date = new Date(creationDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    let text = `
════════════════════════════════════════════════════════════════
                      SPLIT SHEET AGREEMENT
════════════════════════════════════════════════════════════════

TRACK TITLE: ${trackTitle.toUpperCase()}
CREATION DATE: ${date}

────────────────────────────────────────────────────────────────
                        WRITER INFORMATION
────────────────────────────────────────────────────────────────
`;

    writers.forEach((writer, index) => {
      text += `
WRITER ${index + 1}:
  Legal Name:    ${writer.legalName}
  PRO:           ${writer.pro}
  IPI Number:    ${writer.ipi}
  Split:         ${writer.split}%
`;
    });

    text += `
────────────────────────────────────────────────────────────────
                          TOTAL: 100%
────────────────────────────────────────────────────────────────

By signing below, each party acknowledges and agrees to the
ownership splits outlined above for all royalties generated
by the composition titled "${trackTitle}".

────────────────────────────────────────────────────────────────
                          SIGNATURES
────────────────────────────────────────────────────────────────
`;

    writers.forEach((writer, index) => {
      text += `
WRITER ${index + 1} (${writer.legalName}):

Signature: _________________________________  Date: ___________
`;
    });

    text += `
════════════════════════════════════════════════════════════════
          Generated by ItzGreen Music Publishing
                  leaver@itzgreenmusic.com
════════════════════════════════════════════════════════════════
`;

    return text;
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(getSplitSheetText());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const downloadAsText = () => {
    const text = getSplitSheetText();
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `SplitSheet_${trackTitle.replace(/\s+/g, "_")}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const totalSplit = calculateTotalSplit();

  return (
    <main className="min-h-screen bg-[rgb(17,17,19)] pt-28 pb-20 relative overflow-hidden" data-testid="split-sheet-page">
      {/* Background Grid */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage:
            "linear-gradient(rgb(218,255,1) 1px, transparent 1px), linear-gradient(90deg, rgb(218,255,1) 1px, transparent 1px)",
          backgroundSize: "50px 50px",
        }}
      />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 md:px-8 relative z-10">
        {/* Back Link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[rgb(161,161,170)] text-xs tracking-[0.2em] hover:text-[rgb(218,255,1)] transition-colors mb-10 sm:mb-12"
          style={{ fontFamily: "'Inter', sans-serif" }}
          data-testid="back-to-portal-link"
        >
          <ArrowLeft size={14} />
          BACK TO PORTAL
        </Link>

        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10 sm:mb-12"
        >
          <Badge
            variant="outline"
            className="border-[rgb(218,255,1)] text-[rgb(218,255,1)] bg-[rgba(218,255,1,0.05)] mb-6 px-4 py-1.5 text-[10px] sm:text-xs tracking-[0.2em] rounded-md"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <FileText size={12} className="mr-2" />
            LEGAL DOCUMENTATION
          </Badge>

          <h1
            className="text-white text-3xl sm:text-4xl md:text-5xl font-bold mb-4"
            style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: "0.02em" }}
          >
            SPLIT-SHEET <span className="text-[rgb(218,255,1)]">GENERATOR</span>
          </h1>
          <p
            className="text-[rgb(161,161,170)] text-sm sm:text-base max-w-lg mx-auto leading-relaxed"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Generate a standardized ownership agreement for co-written compositions. 
            All parties must sign before catalog submission.
          </p>
        </motion.div>

        <AnimatePresence mode="wait">
          {!generated ? (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* Track Info */}
              <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.08)] rounded-xl p-5 sm:p-6 mb-4">
                <h3
                  className="text-[rgb(218,255,1)] text-sm font-semibold tracking-[0.1em] mb-4"
                  style={{ fontFamily: "'Oswald', sans-serif" }}
                >
                  TRACK INFORMATION
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="text-[rgb(161,161,170)] text-[10px] tracking-[0.15em] block mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                      TRACK TITLE *
                    </label>
                    <Input
                      value={trackTitle}
                      onChange={(e) => setTrackTitle(e.target.value)}
                      placeholder="Enter composition title"
                      data-testid="track-title-input"
                      className="bg-[rgb(40,42,45)] border-[rgb(63,63,63)] text-white placeholder:text-[rgb(80,80,90)] focus:border-[rgb(218,255,1)] focus:ring-[rgba(218,255,1,0.1)]"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-[rgb(161,161,170)] text-[10px] tracking-[0.15em] block mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                      CREATION DATE *
                    </label>
                    <Input
                      type="date"
                      value={creationDate}
                      onChange={(e) => setCreationDate(e.target.value)}
                      data-testid="creation-date-input"
                      className="bg-[rgb(40,42,45)] border-[rgb(63,63,63)] text-white focus:border-[rgb(218,255,1)] focus:ring-[rgba(218,255,1,0.1)]"
                    />
                  </div>
                </div>
              </div>

              {/* Writers */}
              <div className="space-y-4 mb-6">
                <AnimatePresence>
                  {writers.map((writer, index) => (
                    <WriterForm
                      key={index}
                      writer={writer}
                      index={index}
                      onChange={handleWriterChange}
                      onRemove={removeWriter}
                      canRemove={writers.length > 2}
                    />
                  ))}
                </AnimatePresence>
              </div>

              {/* Add Writer Button */}
              {writers.length < 6 && (
                <button
                  onClick={addWriter}
                  data-testid="add-writer-btn"
                  className="w-full py-3 border-2 border-dashed border-[rgb(63,63,63)] rounded-xl text-[rgb(161,161,170)] text-xs tracking-[0.1em] hover:border-[rgb(218,255,1)] hover:text-[rgb(218,255,1)] transition-colors flex items-center justify-center gap-2 mb-6"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  <Plus size={14} />
                  ADD WRITER (MAX 6)
                </button>
              )}

              {/* Split Total Indicator */}
              <div className={`flex items-center justify-between p-4 rounded-xl mb-6 transition-colors ${
                totalSplit === 100 
                  ? "bg-[rgba(74,222,128,0.1)] border border-[rgba(74,222,128,0.3)]" 
                  : splitError
                    ? "bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.3)]"
                    : "bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.08)]"
              }`}>
                <span className="text-[rgb(161,161,170)] text-xs tracking-[0.1em]" style={{ fontFamily: "'Inter', sans-serif" }}>
                  TOTAL OWNERSHIP
                </span>
                <span className={`text-lg font-bold ${
                  totalSplit === 100 
                    ? "text-[rgb(74,222,128)]" 
                    : splitError
                      ? "text-red-400"
                      : "text-white"
                }`} style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {totalSplit}%
                  {totalSplit !== 100 && (
                    <span className="text-xs font-normal ml-2 text-[rgb(161,161,170)]">
                      (must equal 100%)
                    </span>
                  )}
                </span>
              </div>

              {splitError && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 text-red-400 text-xs mb-4 justify-center"
                >
                  <AlertTriangle size={14} />
                  <span style={{ fontFamily: "'Inter', sans-serif" }}>
                    Splits must total exactly 100%
                  </span>
                </motion.div>
              )}

              {/* Generate Button */}
              <Button
                onClick={generateSplitSheet}
                disabled={!isFormValid()}
                data-testid="generate-split-sheet-btn"
                className="w-full bg-[rgb(218,255,1)] text-[rgb(17,17,19)] font-semibold text-sm tracking-[0.1em] h-14 rounded-xl hover:bg-[rgb(166,190,21)] hover:shadow-[0_0_30px_rgba(218,255,1,0.4)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                <FileText size={18} className="mr-2" />
                GENERATE SPLIT SHEET
              </Button>
            </motion.div>
          ) : (
            <motion.div
              key="output"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="split-sheet-output"
            >
              {/* Success Header */}
              <div className="text-center mb-6">
                <div className="inline-flex items-center gap-2 bg-[rgba(74,222,128,0.1)] border border-[rgba(74,222,128,0.3)] rounded-xl px-5 py-3">
                  <CheckCircle size={20} className="text-[rgb(74,222,128)]" />
                  <span className="text-[rgb(74,222,128)] text-sm font-semibold tracking-[0.1em]" style={{ fontFamily: "'Inter', sans-serif" }}>
                    SPLIT SHEET GENERATED
                  </span>
                </div>
              </div>

              {/* Output Preview */}
              <div 
                ref={outputRef}
                className="bg-[rgb(20,20,22)] border border-[rgb(63,63,63)] rounded-xl p-4 sm:p-6 mb-6 font-mono text-xs sm:text-sm text-[rgb(180,180,180)] whitespace-pre-wrap overflow-x-auto max-h-[400px] overflow-y-auto"
                style={{ lineHeight: 1.6 }}
              >
                {getSplitSheetText()}
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <Button
                  onClick={copyToClipboard}
                  data-testid="copy-split-sheet-btn"
                  className="bg-[rgb(63,63,63)] text-white font-semibold text-xs tracking-[0.1em] h-12 rounded-xl hover:bg-[rgb(80,80,85)] transition-all"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {copied ? (
                    <>
                      <CheckCircle size={16} className="mr-2 text-[rgb(74,222,128)]" />
                      COPIED!
                    </>
                  ) : (
                    <>
                      <Copy size={16} className="mr-2" />
                      COPY TEXT
                    </>
                  )}
                </Button>
                <Button
                  onClick={downloadAsText}
                  data-testid="download-split-sheet-btn"
                  className="bg-[rgb(218,255,1)] text-[rgb(17,17,19)] font-semibold text-xs tracking-[0.1em] h-12 rounded-xl hover:bg-[rgb(166,190,21)] hover:shadow-[0_0_20px_rgba(218,255,1,0.3)] transition-all"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  <Download size={16} className="mr-2" />
                  DOWNLOAD .TXT
                </Button>
              </div>

              {/* Edit Button */}
              <button
                onClick={() => setGenerated(false)}
                className="w-full text-[rgb(161,161,170)] text-xs tracking-[0.1em] hover:text-[rgb(218,255,1)] transition-colors py-3"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                ← EDIT SPLIT SHEET
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <Separator className="bg-[rgb(63,63,63)] my-8" />

        {/* Footer Note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center"
        >
          <p
            className="text-[rgb(100,100,110)] text-xs tracking-[0.1em] leading-relaxed"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            This split sheet is a binding legal document. Ensure all parties review and sign before catalog submission.{" "}
            <a
              href="mailto:leaver@itzgreenmusic.com?subject=Split%20Sheet%20Question"
              className="text-[rgb(218,255,1)] hover:underline"
            >
              Contact A&R
            </a>{" "}
            for assistance.
          </p>
        </motion.div>
      </div>
    </main>
  );
};

export default SplitSheetPage;
